import React, { useRef } from 'react'
import { useTextField } from 'react-aria'
import invariant from 'tiny-invariant'
import { IconError } from '@chilipiper/icons/figma-generated/src/icon'
import { typeValidator } from '@chilipiper/utils'
import { Text } from '../../../core-components'
import { Icon } from '../../../core-components/icon/Icon'
import { validatePositioningProps } from '../../../helpers/validatePositioningProps'
import { FocusableHandlerProps, PositioningProps } from '../../types'
import { SettingLabel, SettingLabelContainer } from '../../wayfinding/setting-label/SettingLabel'
import {
  BaseTextAreaAutoExpander,
  BaseTextArea,
  BaseTextAreaWrapper,
} from '../base-textarea/BaseTextArea'

type Variant = 'regular' | 'ghost'

type AllowedSize =
  | 8 // min 40px
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28 // max 880px

type Props = {
  'data-id'?: string
  'data-test-id'?: string
  errorMessage?: string
  isDisabled?: boolean
  isRequired?: boolean
  label: React.ReactNode
  maxHeight?: AllowedSize
  minHeight?: AllowedSize
  name?: string
  onChange: (value: string) => void
  placeholder: string
  value: string
  variant?: Variant
  submitOnEnter?: boolean
  size?: 'regular' | 'large'
} & PositioningProps &
  FocusableHandlerProps<HTMLTextAreaElement>

export const TextArea = ({
  'data-id': dataId,
  'data-test-id': testId,
  errorMessage,
  isDisabled,
  isRequired,
  label,
  name,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  value,
  variant = 'regular',
  maxHeight = 19,
  minHeight = 11,
  submitOnEnter,
  size = 'regular',
  ...positioningProps
}: Props) => {
  validatePositioningProps(positioningProps, 'TextArea')

  const ref = useRef<HTMLTextAreaElement>(null)
  const { description, text } = parseLabel(label)
  const {
    descriptionProps,
    errorMessageProps,
    inputProps: { color: _, ...inputProps },
    labelProps,
  } = useTextField(
    {
      description: description,
      errorMessage: errorMessage,
      inputElementType: 'textarea',
      isDisabled: isDisabled,
      isRequired: isRequired,
      label: text,
      name,
      onChange,
      onBlur: onBlur as React.FocusEventHandler<unknown>,
      onFocus: onFocus as React.FocusEventHandler<unknown>,
      placeholder,
      type: 'text',
      value,
      onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (submitOnEnter && event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault()
          event.currentTarget.closest('form')?.requestSubmit()
        }
      },
    },
    ref
  )

  return (
    <SettingLabelContainer
      isRequired={isRequired}
      descriptionProps={descriptionProps}
      label={label}
      labelElement='label'
      labelProps={labelProps}
      {...positioningProps}
    >
      <BaseTextAreaWrapper
        maxHeight={maxHeight}
        minHeight={minHeight}
        position='relative'
        w='100%'
        gridTemplateRows='minmax(0, 1fr)'
        variant={variant}
        hasError={!!errorMessage}
      >
        <BaseTextArea
          gridArea='1 / 1 / 1 / 1'
          as='textarea'
          ref={ref}
          py={PADDING_Y}
          pl={PADDING_LEFT}
          pr={PADDING_RIGHT}
          w='100%'
          variant={variant}
          rows={1}
          {...inputProps}
          data-id={dataId}
          data-test-id={testId}
          hasError={!!errorMessage}
          $size={size}
        />
        <BaseTextAreaAutoExpander
          gridArea='1 / 1 / 1 / 1'
          py={PADDING_Y}
          pl={PADDING_LEFT}
          pr={PADDING_RIGHT}
          aria-hidden
          $size={size}
        >
          {value}
        </BaseTextAreaAutoExpander>
        {!!errorMessage && (
          <Icon
            color='fg/caution'
            size={4}
            icon={IconError}
            position='absolute'
            right={5}
            top={PADDING_Y}
          />
        )}
      </BaseTextAreaWrapper>
      {!!errorMessage && (
        <Text
          {...errorMessageProps}
          color='fg/caution'
          data-test-id='textarea-error-message'
          display='block'
          mt={2}
          textStyle='body-multiline-tight'
        >
          {errorMessage}
        </Text>
      )}
    </SettingLabelContainer>
  )
}

const PADDING_Y = '9px'
const PADDING_LEFT = 5
const PADDING_RIGHT = 10

const parseLabel = (label: React.ReactNode) => {
  invariant(
    typeValidator(TextAreaLabel)(label),
    `TextArea: label must be an TextArea.Label component`
  )

  return {
    text: label.props.children,
    description: label.props.description,
  }
}

const TextAreaLabel = (props: React.ComponentProps<typeof SettingLabel>) => (
  <SettingLabel {...props} />
)

TextArea.Label = TextAreaLabel
